import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import AgingBalanceRow from './AgingBalanceRow';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ agingBalanceList, showFolio }) =>
  agingBalanceList.map((item, index) => (
    <AgingBalanceRow
      key={`${item.clientId}-${item.contractId || index}`}
      {...item}
      showFolio={showFolio}
    />
  ));

Tbody.propTypes = {
  agingBalanceList: PropTypes.array.isRequired,
  showFolio: PropTypes.bool.isRequired
};

const AgingBalanceTable = ({
  className,
  agingBalanceList,
  count,
  page,
  handleChangePage,
  loading,
  showFolio,
  ...rest
}) => {
  const { t } = useTranslation();

  const theads = useMemo(
    () => () => (
      <TableRow>
        {showFolio && (
          <TableCell
            variant="head"
            key="folio"
            sx={{ width: '5%' }}
            align="center"
          >
            {t('COMMON.FOLIO')}
          </TableCell>
        )}
        <TableCell
          variant="head"
          key="client"
          sx={{ width: showFolio ? '15%' : '15%' }}
          align="left"
        >
          {t('COMMON.CLIENT')}
        </TableCell>
        <TableCell
          variant="head"
          key="totalOverdue"
          align="center"
          sx={{ width: '13%' }}
        >
          {t('AGING_BALANCE.TOTAL_BALANCE')}
        </TableCell>
        <TableCell
          variant="head"
          key="upToDateBalance"
          align="center"
          sx={{ width: '13%' }}
        >
          {t('AGING_BALANCE.UP_TO_DATE_BALANCE')}
        </TableCell>
        <TableCell
          variant="head"
          key="overdue_1_30"
          align="center"
          sx={{ width: '12%' }}
        >
          {t('AGING_BALANCE.OVERDUE_1_30')}
        </TableCell>
        <TableCell
          variant="head"
          key="overdue_31_60"
          align="center"
          sx={{ width: '12%' }}
        >
          {t('AGING_BALANCE.OVERDUE_31_60')}
        </TableCell>
        <TableCell
          variant="head"
          key="overdue_61_90"
          align="center"
          sx={{ width: '12%' }}
        >
          {t('AGING_BALANCE.OVERDUE_61_90')}
        </TableCell>
        <TableCell
          variant="head"
          key="overdue_91_120"
          align="center"
          sx={{ width: '12%' }}
        >
          {t('AGING_BALANCE.OVERDUE_91_120')}
        </TableCell>
        <TableCell
          variant="head"
          key="overdue_over_120"
          align="center"
          sx={{ width: '12%' }}
        >
          {t('AGING_BALANCE.OVERDUE_OVER_120')}
        </TableCell>
      </TableRow>
    ),
    [t, showFolio]
  );

  const tbody = useMemo(
    () => () => (
      <Tbody agingBalanceList={agingBalanceList} showFolio={showFolio} />
    ),
    [agingBalanceList, showFolio]
  );

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
        labelDisplayedRows={({ from, to, count: c }) => {
          return `${from}-${to} ${t('COMMON.OF')} ${c}`;
        }}
      />
    ),
    [page, count, handleChangePage, t]
  );

  return (
    <Table
      {...rest}
      variant="outlined"
      tbody={tbody}
      theads={theads}
      pagination={pagination}
      loading={loading}
    />
  );
};

AgingBalanceTable.propTypes = {
  className: PropTypes.string,
  agingBalanceList: PropTypes.array.isRequired,
  count: PropTypes.number,
  page: PropTypes.number,
  handleChangePage: PropTypes.func,
  loading: PropTypes.bool,
  showFolio: PropTypes.bool
};

AgingBalanceTable.defaultProps = {
  count: 0,
  showFolio: false
};

export default AgingBalanceTable;
